import { findEffectivePermissions } from '@api/featurePermission';
import useToastContext from '@contexts/ToastContext/hook';
import {
  EffectivePermissionsMap,
  FeatureName,
  UserPermissions,
} from '@interfaces/featurePermission';
import { Product } from '@interfaces/product';
import { VibLoadingOverlay } from '@vibTheme/components/VibLoadingOverlay';
import { PermissionAlert } from '@components/PermissionAlert';
import {
  PropsWithChildren,
  createContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export type FeaturePermissionsContextValue = {
  getUserPermissions: (
    featureName: FeatureName,
    productId: Product['id'],
  ) => UserPermissions;
  /**
   * Rebusca a matriz de permissão.
   *
   * Exposta porque o aceite de convite muda o acesso da pessoa NO SERVIDOR, com o app já montado:
   * este provider carrega os dados uma vez, no efeito de montagem, e sem um refresh a comunidade
   * recém-aceita continuaria invisível até um reload manual. É o mesmo que o `GroupsContext` já
   * expõe com `fetchGroups`.
   */
  /** `false` quando a busca falhou — quem depende do estado novo precisa saber. */
  refreshPermissions: () => Promise<boolean>;
};

/** A altura do aviso de falha, para quem também é `fixed` embaixo sair de cima dele. */
export const ALTURA_DO_AVISO = '--permission-alert-height';

export const FeaturePermissionsContext = createContext(
  {} as FeaturePermissionsContextValue,
);

export const FeaturePermissionsContextProvider = (props: PropsWithChildren) => {
  const [efetivas, setEfetivas] = useState<EffectivePermissionsMap>({});
  /*
    A busca falhou, e a tela diz isso em vez de fingir que resolveu (#337, opção C).

    Havia aqui um plano B que caía no cálculo antigo no browser. Ele resolvia o problema errado:
    aquele cálculo IGNORA grant e não conhece colaborador, então a queda rebaixava em silêncio quem
    entra pela Central — a permissão concedida simplesmente não valia, sem nada na tela dizendo por
    quê. Rebaixamento silencioso é pior que erro visível numa camada de autorização.

    O que o plano B protegia continua protegido: o provider embrulha todas as páginas autenticadas,
    e travar no overlay deixaria o app inutilizável. Por isso a falha não trava — ela libera as
    páginas com permissão NENHUMA e um aviso, que é o estado honesto de "não sei o que você pode".
  */
  const [falhou, setFalhou] = useState(false);
  const avisoRef = useRef<HTMLDivElement>(null);

  /*
    Publica a altura do aviso numa variável de CSS, como o `FeeChangeBanner` faz com a dele.

    Ele é `fixed` embaixo, e o `BackToTop` também — e com `z-sidebar`, ou seja, ACIMA. Sem isto o
    botão é pintado por cima do aviso, e em tela estreita, onde o texto quebra em várias linhas, ele
    esconde justamente a explicação. Medido com `getBoundingClientRect`, e não `offsetHeight`, que
    arredonda para inteiro e deixaria um vão de subpixel.
  */
  useEffect(() => {
    const raiz = document.documentElement;
    const aviso = avisoRef.current;

    /*
      Sem falha não há o que publicar, e não há o que limpar aqui: a limpeza do efeito ANTERIOR já
      rodou antes desta execução, e é ela que desfaz a variável e o espaço reservado. Limpar de novo
      neste ramo é linha morta — conferi quebrando, e nenhum teste muda.
    */
    if (!falhou || !aviso) return;

    /*
      O aviso é `fixed`, então ele não ocupa altura no documento: no fim de uma página ele fica por
      CIMA do último conteúdo — e em tela estreita, onde o texto quebra em várias linhas, isso cobre
      botão de formulário. Reservar o espaço no `body` resolve para as duas famílias de layout sem
      este provider precisar saber qual está em uso, e sem acrescentar um `div` que mudaria a
      estrutura de quem depende de filho direto.
    */
    document.body.style.setProperty(
      'padding-bottom',
      `var(${ALTURA_DO_AVISO}, 0px)`,
    );

    const medir = () =>
      raiz.style.setProperty(
        ALTURA_DO_AVISO,
        `${aviso.getBoundingClientRect().height}px`,
      );

    medir();

    const observador = new ResizeObserver(medir);
    observador.observe(aviso);

    return () => {
      observador.disconnect();
      raiz.style.removeProperty(ALTURA_DO_AVISO);
      document.body.style.removeProperty('padding-bottom');
    };
  }, [falhou]);
  const [isStateReady, setIsStateReady] = useState(false);

  const toast = useToastContext();

  const fetchPermissions = async () => {
    try {
      setEfetivas(await findEffectivePermissions());
      setFalhou(false);
      setIsStateReady(true);

      return true;
    } catch (err) {
      setEfetivas({});
      setFalhou(true);
      /*
        `isStateReady` vira true MESMO na falha: sem isso o provider fica no overlay e o app inteiro
        para. A pessoa navega, e cada tela mostra o que mostra para quem não pode nada — com o
        aviso explicando que a permissão não pôde ser carregada.
      */
      setIsStateReady(true);

      toast.handleError(
        err,
        'Não foi possível carregar suas permissões. Recarregue a página; se continuar, fale com o suporte.',
      );

      /*
        Mesmo motivo do `fetchGroups`: quem chama esperando estado novo — o aceite de convite —
        precisa saber que ele não veio, em vez de anunciar sucesso sobre estado velho.
      */
      return false;
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const getUserPermissions: FeaturePermissionsContextValue['getUserPermissions'] =
    (featureName, productId) => {
      const efetiva = efetivas[String(productId)];

      if (efetiva) {
        const acoes = efetiva.permissions[featureName] ?? [];

        return {
          canView: acoes.includes('view'),
          canCreate: acoes.includes('create'),
          canUpdate: acoes.includes('update'),
          canRemove: acoes.includes('delete'),
          canExport: acoes.includes('export'),
        };
      }

      /*
        Sem entrada no mapa, NADA é permitido.

        São dois casos, e os dois querem a mesma resposta: produto que a pessoa não alcança, e mapa
        que não veio. O primeiro é vazio por definição; o segundo é desconhecido, e desconhecido em
        autorização se resolve fechando. Antes daqui saía a matriz do papel, que respondia com
        confiança uma pergunta que ninguém tinha como responder.
      */
      return {
        canView: false,
        canCreate: false,
        canUpdate: false,
        canRemove: false,
        canExport: false,
      };
    };

  if (!isStateReady) return <VibLoadingOverlay />;

  return (
    <FeaturePermissionsContext.Provider
      value={{
        getUserPermissions,
        refreshPermissions: fetchPermissions,
      }}
    >
      {/*
        O aviso é um COMPONENTE em `components/`, e não JSX aqui, porque o `content` do Tailwind não
        varre `contexts` — classe escrita neste arquivo só existe no CSS final por acidente, quando
        algum arquivo varrido usa a mesma. O porquê de cada escolha visual está lá.

        O que fica aqui é a decisão de QUANDO ele aparece, e onde: acima de todas as páginas, porque
        sem permissão toda tela responde como se a pessoa não pudesse nada — menu vazio, cards
        escondidos —, o que é indistinguível de "você perdeu acesso". Um aviso só, no lugar por onde
        tudo passa, explica o app inteiro de uma vez.
      */}
      {falhou && <PermissionAlert ref={avisoRef} />}

      {props.children}
    </FeaturePermissionsContext.Provider>
  );
};
